/*
 * BizMark Toolkit sales page: progressive enhancements only.
 * Everything on the page, including purchasing, works without this file.
 * Handles: screenshot tabs, variant/price sync, add-to-cart loading state,
 * sticky mobile bar visibility, smooth anchor scrolling and click-to-play video.
 */
(function () {
  'use strict';

  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function prefersReducedMotion() {
    return motionQuery ? motionQuery.matches : false;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /* ---------- Tabs ---------- */

  function initTabs(container) {
    var tablist = container.querySelector('[data-bm-tablist]');
    var tabs = toArray(container.querySelectorAll('[data-bm-tab]'));
    var panels = toArray(container.querySelectorAll('[data-bm-panel]'));

    if (!tablist || tabs.length < 2 || tabs.length !== panels.length) return;

    tablist.hidden = false;
    container.classList.add('is-enhanced');

    panels.forEach(function (panel, index) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabs[index].id);
      panel.setAttribute('tabindex', '0');
      var title = panel.querySelector('.bm-sales-tabs__panel-title');
      if (title) title.classList.add('bm-sales-visually-hidden');
    });

    function select(index, moveFocus) {
      tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        panels[i].hidden = !active;
      });
      if (moveFocus) {
        tabs[index].focus();
        revealTab(tabs[index]);
      }
    }

    /* Scroll only the tab strip horizontally, never the page. */
    function revealTab(tab) {
      if (tablist.scrollWidth <= tablist.clientWidth) return;
      var listBox = tablist.getBoundingClientRect();
      var tabBox = tab.getBoundingClientRect();
      if (tabBox.left < listBox.left) tablist.scrollLeft -= listBox.left - tabBox.left + 16;
      else if (tabBox.right > listBox.right) tablist.scrollLeft += tabBox.right - listBox.right + 16;
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        select(index, false);
        revealTab(tab);
      });

      tab.addEventListener('keydown', function (event) {
        var isRtl = window.getComputedStyle(tablist).direction === 'rtl';
        var next = isRtl ? 'ArrowLeft' : 'ArrowRight';
        var prev = isRtl ? 'ArrowRight' : 'ArrowLeft';
        var target = null;

        if (event.key === next) target = (index + 1) % tabs.length;
        else if (event.key === prev) target = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;

        if (target !== null) {
          event.preventDefault();
          select(target, true);
        }
      });
    });

    container.bmSelectPanel = function (blockId) {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-block-id') === blockId) {
          select(i, false);
          return;
        }
      }
    };

    var initial = 0;
    tabs.forEach(function (tab, i) {
      if (tab.getAttribute('aria-selected') === 'true') initial = i;
    });
    select(initial, false);
  }

  /* ---------- Product: variants, prices, loading state ---------- */

  function initProduct(root) {
    var form = root.querySelector('[data-bm-form]');
    var dataEl = root.querySelector('[data-bm-variants]');
    if (!form || !dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    var idInput = form.querySelector('[name="id"]');
    var select = form.querySelector('[data-bm-variant-select]');
    var status = form.querySelector('[data-bm-status]');
    var textAvailable = root.getAttribute('data-bm-text-available') || '';
    var textSoldOut = root.getAttribute('data-bm-text-sold-out') || '';
    var textAdding = root.getAttribute('data-bm-text-adding') || '';

    function findVariant(id) {
      var numeric = Number(id);
      for (var i = 0; i < variants.length; i++) {
        if (Number(variants[i].id) === numeric) return variants[i];
      }
      return null;
    }

    function getButtons() {
      return toArray(root.querySelectorAll('[data-bm-add]'));
    }

    function render(variant) {
      if (!variant) return;

      toArray(root.querySelectorAll('[data-bm-price]')).forEach(function (el) {
        el.textContent = variant.price;
      });

      toArray(root.querySelectorAll('[data-bm-compare-wrap]')).forEach(function (wrap) {
        var compare = wrap.querySelector('[data-bm-compare]');
        if (variant.compareAt) {
          if (compare) compare.textContent = variant.compareAt;
          wrap.hidden = false;
        } else {
          wrap.hidden = true;
        }
      });

      var availability = root.querySelector('[data-bm-availability]');
      if (availability) {
        availability.setAttribute('data-state', variant.available ? 'available' : 'sold-out');
        var availabilityText = availability.querySelector('[data-bm-availability-text]');
        if (availabilityText) availabilityText.textContent = variant.available ? textAvailable : textSoldOut;
      }

      toArray(root.querySelectorAll('[data-bm-availability-mirror]')).forEach(function (el) {
        el.textContent = variant.available ? textAvailable : textSoldOut;
      });

      getButtons().forEach(function (button) {
        button.disabled = !variant.available;
        var label = button.querySelector('[data-bm-add-label]');
        if (label) {
          label.textContent = variant.available
            ? button.getAttribute('data-label-available')
            : button.getAttribute('data-label-sold-out');
        }
      });

      var dynamicCheckout = root.querySelector('[data-bm-dynamic-checkout]');
      if (dynamicCheckout) dynamicCheckout.hidden = !variant.available;
    }

    function updateUrl(id) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        /* URL API unavailable: keep current URL. */
      }
    }

    if (select) {
      select.addEventListener('change', function () {
        var variant = findVariant(select.value);
        render(variant);
        if (variant) updateUrl(variant.id);
      });
    }

    function setLoading(isLoading) {
      getButtons().forEach(function (button) {
        button.classList.toggle('is-loading', isLoading);
        if (isLoading) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }
      });
      if (status) status.textContent = isLoading ? textAdding : '';
    }

    form.addEventListener('submit', function (event) {
      if (form.getAttribute('data-bm-submitting') === 'true') {
        event.preventDefault();
        return;
      }
      var current = idInput ? findVariant(idInput.value) : null;
      if (current && !current.available) {
        event.preventDefault();
        return;
      }
      form.setAttribute('data-bm-submitting', 'true');
      setLoading(true);
    });

    window.addEventListener('pageshow', function () {
      form.removeAttribute('data-bm-submitting');
      setLoading(false);
    });
  }

  /* ---------- Sticky mobile bar ---------- */

  function initSticky(root) {
    var bar = root.querySelector('[data-bm-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var targets = toArray(root.querySelectorAll('[data-bm-sticky-hide]'));
    if (!targets.length) return;

    var visible = new Set();
    bar.classList.add('is-hidden');

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      var hide = visible.size > 0;
      if (hide && bar.contains(document.activeElement)) return;
      bar.classList.toggle('is-hidden', hide);
    }, { threshold: 0.05 });

    targets.forEach(function (target) {
      observer.observe(target);
    });

    root.bmObservers = (root.bmObservers || []).concat(observer);
  }

  /* ---------- Smooth anchor navigation ---------- */

  function initAnchors(root) {
    root.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[data-bm-scroll]') : null;
      if (!link) return;

      var hash = link.getAttribute('href') || '';
      if (hash.charAt(0) !== '#') return;

      var target = document.getElementById(hash.slice(1));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });

      if (window.history && typeof window.history.replaceState === 'function') {
        try {
          window.history.replaceState(null, '', hash);
        } catch (error) {
          /* Ignore environments that block history updates. */
        }
      }
    });
  }

  /* ---------- Click-to-play video (never autoplays on load) ---------- */

  function initVideo(root) {
    toArray(root.querySelectorAll('[data-bm-video]')).forEach(function (link) {
      link.addEventListener('click', function (event) {
        var type = link.getAttribute('data-video-type');
        var id = encodeURIComponent(link.getAttribute('data-video-id') || '');
        var src = '';

        if (!id) return;
        if (type === 'youtube') src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0';
        else if (type === 'vimeo') src = 'https://player.vimeo.com/video/' + id + '?autoplay=1&dnt=1';
        if (!src) return;

        event.preventDefault();

        var host = link.closest('[data-bm-video-host]') || link.parentNode;
        var iframe = document.createElement('iframe');
        iframe.className = 'bm-sales-video-iframe';
        iframe.src = src;
        iframe.title = link.getAttribute('data-video-title') || '';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        iframe.setAttribute('allowfullscreen', '');

        while (host.firstChild) host.removeChild(host.firstChild);
        host.appendChild(iframe);
        iframe.focus();
      });
    });
  }

  /* ---------- Lifecycle ---------- */

  function initRoot(root) {
    if (!root || root.getAttribute('data-bm-ready') === 'true') return;
    root.setAttribute('data-bm-ready', 'true');
    root.classList.add('bm-sales-js');

    toArray(root.querySelectorAll('[data-bm-tabs]')).forEach(initTabs);
    initProduct(root);
    initSticky(root);
    initAnchors(root);
    initVideo(root);
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll('[data-bm-sales]')).forEach(initRoot);
  }

  function destroy(scope) {
    toArray((scope || document).querySelectorAll('[data-bm-sales]')).forEach(function (root) {
      (root.bmObservers || []).forEach(function (observer) {
        observer.disconnect();
      });
      root.bmObservers = [];
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var panel = event.target;
    if (!panel || !panel.hasAttribute || !panel.hasAttribute('data-bm-panel')) return;
    var container = panel.closest('[data-bm-tabs]');
    if (container && typeof container.bmSelectPanel === 'function') {
      container.bmSelectPanel(panel.getAttribute('data-block-id'));
    }
  });
})();
